import { useQuery } from '@tanstack/react-query';
import {
  CalendarIcon,
  ClockIcon,
  ExternalLinkIcon,
  FileTextIcon,
  LinkIcon,
  MailIcon,
  StickyNoteIcon,
  UserIcon,
  UsersIcon,
} from 'lucide-react';

import { Badge } from '@/components/badge/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Skeleton } from '@/components/skeleton/skeleton';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import { getSession } from '@/features/sessions/api/get-session.api';
import type { SessionDTO } from '@/features/sessions/schemas/get-sessions.schema';

const dateFormatter = new Intl.DateTimeFormat('id-ID', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function formatDate(iso: string | null | undefined) {
  if (!iso) return '-';
  return dateFormatter.format(new Date(iso));
}

function statusBadgeVariant(status: string | null | undefined) {
  if (!status) return 'neutral' as const;
  const normalized = status.toLowerCase();
  if (normalized === 'completed') return 'success' as const;
  if (normalized === 'incoming') return 'info' as const;
  return 'neutral' as const;
}

export interface DetailSessionDialogProps extends BaseDialogProps {
  sessionId: string;
}

export default function DetailSessionDialog({
  dialog,
  sessionId,
}: DetailSessionDialogProps) {
  const { closeDetailSessionDialog } = useV2DialogContext();

  const getSessionQuery = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => getSession(sessionId),
  });

  const session = getSessionQuery.data;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeDetailSessionDialog();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Detail Sesi Mentoring</DialogTitle>
          <DialogDescription>
            Informasi lengkap tentang sesi mentoring ini.
          </DialogDescription>
        </DialogHeader>

        {getSessionQuery.isPending ? (
          <DetailSessionSkeleton />
        ) : getSessionQuery.isError || !session ? (
          <p className="text-sm text-destructive">
            Gagal memuat data sesi mentoring. Silakan coba lagi.
          </p>
        ) : (
          <div className="flex flex-col gap-6">
            <DetailSessionHeader session={session} />
            <DetailSessionParties session={session} />
            <DetailSessionInfo session={session} />
            {(session.note || session.sessionNote) && (
              <DetailSessionNotes session={session} />
            )}
            <DetailSessionMeta session={session} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface DetailSessionSectionProps {
  session: SessionDTO;
}

function DetailSessionHeader({ session }: DetailSessionSectionProps) {
  return (
    <div className="flex flex-col gap-2">
      <p className="text-base font-semibold text-foreground">
        {session.topicTitle?.title ?? 'Tanpa Topik'}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant={statusBadgeVariant(session.status)}>
          {session.status ?? 'Tidak diketahui'}
        </Badge>
        <Badge variant="secondary" className="lowercase">
          {session.type}
        </Badge>
      </div>
    </div>
  );
}

function DetailSessionParties({ session }: DetailSessionSectionProps) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <PartyCard
        icon={<UserIcon className="size-4" />}
        label="User"
        name={session.user?.fullName ?? '-'}
        email={session.user?.email}
      />
      <PartyCard
        icon={<UsersIcon className="size-4" />}
        label="Mentor"
        name={session.mentor?.fullName ?? '-'}
        email={session.mentor?.email}
      />
    </div>
  );
}

interface PartyCardProps {
  icon: React.ReactNode;
  label: string;
  name: string;
  email?: string;
}

function PartyCard({ icon, label, name, email }: PartyCardProps) {
  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-border bg-muted/30 p-3">
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span>{icon}</span>
        <span>{label}</span>
      </div>
      <p className="text-sm font-medium text-foreground">{name}</p>
      {email && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <MailIcon className="size-3" />
          {email}
        </p>
      )}
    </div>
  );
}

function DetailSessionInfo({ session }: DetailSessionSectionProps) {
  return (
    <div className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-muted/30 p-4 sm:grid-cols-2">
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Mulai"
        value={formatDate(session.sessionStartDate)}
      />
      <InfoRow
        icon={<ClockIcon className="size-4" />}
        label="Selesai"
        value={formatDate(session.sessionEndDate)}
      />
      {session.schedule && (
        <InfoRow
          icon={<CalendarIcon className="size-4" />}
          label="Jadwal"
          value={`${session.schedule.scheduleDay} ${session.schedule.scheduleTime}`}
        />
      )}
      <InfoRow
        icon={<LinkIcon className="size-4" />}
        label="Link Meeting"
        value={
          session.linkMeet ? (
            <a
              href={session.linkMeet}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary underline underline-offset-2 hover:opacity-80"
            >
              <ExternalLinkIcon className="size-3" />
              Buka tautan
            </a>
          ) : (
            '-'
          )
        }
      />
    </div>
  );
}

function DetailSessionNotes({ session }: DetailSessionSectionProps) {
  return (
    <div className="flex flex-col gap-3">
      {session.note && (
        <NoteBlock
          icon={<StickyNoteIcon className="size-4" />}
          label="Catatan"
          content={session.note}
        />
      )}
      {session.sessionNote && (
        <NoteBlock
          icon={<FileTextIcon className="size-4" />}
          label="Catatan Sesi"
          content={session.sessionNote}
        />
      )}
    </div>
  );
}

interface NoteBlockProps {
  icon: React.ReactNode;
  label: string;
  content: string;
}

function NoteBlock({ icon, label, content }: NoteBlockProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <span>{icon}</span>
        {label}
      </p>
      <p className="text-sm leading-relaxed text-foreground">{content}</p>
    </div>
  );
}

function DetailSessionMeta({ session }: DetailSessionSectionProps) {
  return (
    <div className="flex flex-col gap-2 border-t border-border pt-4">
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Dibuat Pada"
        value={formatDate(session.createdAt)}
      />
      <InfoRow
        icon={<CalendarIcon className="size-4" />}
        label="Diperbarui Pada"
        value={formatDate(session.updatedAt)}
      />
    </div>
  );
}

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}

function InfoRow({ icon, label, value }: InfoRowProps) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-xs text-muted-foreground">{label}</p>
        <div className="wrap-break-word text-sm font-medium text-foreground">
          {value}
        </div>
      </div>
    </div>
  );
}

function DetailSessionSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-5 w-48" />
        <div className="flex gap-2">
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-5 w-16 rounded-full" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Skeleton className="h-20 w-full rounded-lg" />
        <Skeleton className="h-20 w-full rounded-lg" />
      </div>
      <Skeleton className="h-32 w-full rounded-lg" />
    </div>
  );
}
